import { t } from "../../../shared/translation";

type QuestionType = "YesNo" | "MultiSelect" | "OpenText";

type Question = {
    id: number;
    text: string;
    type: QuestionType;
    isRequired: boolean;
    options: string[];
    optionLabels: string[];
    parentQuestionId?: number | null;
    parentAnswer?: string | null;
};

type Answer = {
    questionId: number;
    value?: string;
    values?: string[];
};

const escapeHtml = (value: string): string =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const createButton = (
    text: string,
    click: () => void | Promise<void>,
    variant: "primary" | "secondary" = "primary"
): HTMLButtonElement => {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = text;
    button.className =
        variant === "primary" ? "[ button:main --small ]" : "[ button:word --small ] text-gray-600 !pl-0";
    button.addEventListener("click", () => void click());
    return button;
};

export const initializeFeedbackWidget = async (): Promise<void> => {
    const root = document.querySelector<HTMLElement>("[data-feedback-widget]");
    if (!root) {
        return;
    }

    const toggle = root.querySelector<HTMLButtonElement>("[data-feedback-toggle]");
    const panel = root.querySelector<HTMLElement>("[data-feedback-panel]");
    const closeButton = root.querySelector<HTMLButtonElement>("[data-feedback-close]");
    const content = root.querySelector<HTMLElement>("[data-feedback-content]");
    const actions = root.querySelector<HTMLElement>("[data-feedback-actions]");

    if (!toggle || !panel || !closeButton || !content || !actions) {
        return;
    }

    const answers = new Map<number, Answer>();
    let questions: Question[] = [];
    let currentIndex = 0;
    /** After a successful submit, next time the panel opens we start a fresh survey. */
    let shouldRestartOnOpen = false;

    const answerMatches = (parent: Question, answer: Answer | undefined, trigger: string): boolean => {
        if (!answer) {
            return false;
        }

        if (parent.type === "YesNo") {
            return (answer.value ?? "").trim().toLowerCase() === trigger.trim().toLowerCase();
        }

        if (parent.type === "MultiSelect") {
            return answer.values?.includes(trigger) ?? false;
        }

        return false;
    };

    const isVisible = (question: Question, visiting: Set<number> = new Set()): boolean => {
        if (question.parentQuestionId == null) {
            return true;
        }

        if (!question.parentAnswer || visiting.has(question.id)) {
            return false;
        }

        visiting.add(question.id);
        const parent = questions.find((q) => q.id === question.parentQuestionId);
        const visible =
            !!parent &&
            isVisible(parent, visiting) &&
            answerMatches(parent, answers.get(parent.id), question.parentAnswer);
        visiting.delete(question.id);
        return visible;
    };

    const findVisibleIndex = (from: number, direction: 1 | -1): number => {
        for (let i = from; i >= 0 && i < questions.length; i += direction) {
            const question = questions[i];
            if (question && isVisible(question)) {
                return i;
            }
        }

        return -1;
    };

    const open = () => {
        if (shouldRestartOnOpen) {
            answers.clear();
            currentIndex = 0;
            shouldRestartOnOpen = false;
        }

        root.classList.add("is-open");

        toggle.setAttribute("aria-expanded", "true");
    };

    const close = () => {
        root.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
    };

    const submit = async () => {
        const visibleAnswers = Array.from(answers.values()).filter((answer) => {
            const question = questions.find((q) => q.id === answer.questionId);
            return !!question && isVisible(question);
        });

        const result = await fetch("/api/feedback/submit", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                pageUrl: window.location.href,
                answers: visibleAnswers
            })
        });

        if (!result.ok) {
            renderCurrentQuestion(
                t("DigitalPresence.Feedback.Widget.SubmitError", "Failed to submit. Please try again.")
            );
            return;
        }

        shouldRestartOnOpen = true;
        renderThankYou();
    };

    const updateQuestionActions = () => {
        actions.hidden = false;
        actions.replaceChildren();

        const current = questions[currentIndex];
        if (!current) {
            return;
        }

        const isLast = findVisibleIndex(currentIndex + 1, 1) === -1;
        const showSkip = !current.isRequired && !isLast;
        const showBack = findVisibleIndex(currentIndex - 1, -1) !== -1;

        const back = createButton(
            t("DigitalPresence.Feedback.Widget.Back", "< Back"),
            () => {
                answers.set(current.id, collectAnswer(current));
                currentIndex = Math.max(findVisibleIndex(currentIndex - 1, -1), 0);
                renderCurrentQuestion();
            },
            "secondary"
        );
        back.classList.add("feedback-widget__back");

        const skip = createButton(
            t("DigitalPresence.Feedback.Widget.Skip", "Skip"),
            () => {
                answers.delete(current.id);
                const nextIndex = findVisibleIndex(currentIndex + 1, 1);
                if (nextIndex === -1) {
                    void submit();
                    return;
                }

                currentIndex = nextIndex;
                renderCurrentQuestion();
            },
            "secondary"
        );
        const next = createButton(
            isLast
                ? t("DigitalPresence.Feedback.Widget.Submit", "Submit")
                : t("DigitalPresence.Feedback.Widget.Next", "Next"),
            async () => {
                const question = questions[currentIndex];
                if (!question) {
                    return;
                }

                const answer = collectAnswer(question);
                answers.set(question.id, answer);

                if (question.isRequired && isAnswerEmpty(question, answer)) {
                    renderCurrentQuestion(t("DigitalPresence.Feedback.Widget.Required", "This question is required."));
                    return;
                }

                const nextIndex = findVisibleIndex(currentIndex + 1, 1);
                if (nextIndex === -1) {
                    await submit();
                    return;
                }

                currentIndex = nextIndex;
                renderCurrentQuestion();
            }
        );

        if (showBack) {
            actions.append(back);
        }
        if (showSkip) {
            actions.append(skip, next);
        } else {
            actions.append(next);
        }
    };

    const isAnswerEmpty = (question: Question, answer: Answer): boolean => {
        if (question.type === "MultiSelect") {
            return !answer.values || answer.values.length === 0;
        }

        return !answer.value || answer.value.trim().length === 0;
    };

    const collectAnswer = (question: Question): Answer => {
        if (question.type === "YesNo") {
            const checked = content.querySelector<HTMLInputElement>(`input[name='q-${question.id}']:checked`);
            return { questionId: question.id, value: checked?.value };
        }

        if (question.type === "MultiSelect") {
            const checked = Array.from(
                content.querySelectorAll<HTMLInputElement>(`input[name='q-${question.id}']:checked`)
            ).map((x) => x.value);
            return { questionId: question.id, values: checked };
        }

        const text = content.querySelector<HTMLInputElement>(`input[name='q-${question.id}']`);
        return { questionId: question.id, value: text?.value ?? "" };
    };

    const renderCurrentQuestion = (errorMessage?: string) => {
        const clamped = Math.min(Math.max(0, currentIndex), questions.length - 1);
        const current = questions[clamped];
        const visibleIndex = current && isVisible(current) ? clamped : findVisibleIndex(clamped, 1);
        if (visibleIndex === -1) {
            content.innerHTML = `<p>${escapeHtml(t("DigitalPresence.Feedback.Widget.NoQuestions", "No feedback questions configured."))}</p>`;
            actions.hidden = true;
            return;
        }

        currentIndex = visibleIndex;
        const question = questions[visibleIndex];
        if (!question) {
            return;
        }

        const savedAnswer = answers.get(question.id);
        const requiredMark = question.isRequired ? " *" : "";
        let html = `<div><p><strong>${escapeHtml(question.text)}${requiredMark}</strong></p>`;

        if (question.type === "YesNo") {
            const yesChecked = savedAnswer?.value === "Yes" ? "checked" : "";
            const noChecked = savedAnswer?.value === "No" ? "checked" : "";
            const yesLabel = escapeHtml(t("DigitalPresence.Feedback.Widget.Yes", "Yes"));
            const noLabel = escapeHtml(t("DigitalPresence.Feedback.Widget.No", "No"));
            html += `<div class="feedback-widget__choices">`;
            html += `<label class="feedback-widget__choice"><input type="radio" name="q-${question.id}" value="Yes" ${yesChecked} /><span>${yesLabel}</span></label>`;
            html += `<label class="feedback-widget__choice"><input type="radio" name="q-${question.id}" value="No" ${noChecked} /><span>${noLabel}</span></label>`;
            html += `</div>`;
        } else if (question.type === "MultiSelect") {
            const selected = new Set(savedAnswer?.values ?? []);
            html += `<div class="feedback-widget__multiselect" role="group">`;
            html += question.options
                .map((o, i) => {
                    const value = escapeHtml(o);
                    const label = escapeHtml(question.optionLabels?.[i] ?? o);
                    return `<label class="feedback-widget__choice"><input type="checkbox" name="q-${question.id}" value="${value}" ${selected.has(o) ? "checked" : ""} /><span>${label}</span></label>`;
                })
                .join("");
            html += `</div>`;
        } else {
            const value = savedAnswer?.value ?? "";
            html += `<input type="text" class="form-control" name="q-${question.id}" value="${escapeHtml(value)}" />`;
        }

        if (errorMessage) {
            html += `<p class="feedback-widget__error">${escapeHtml(errorMessage)}</p>`;
        }

        html += "</div>";
        content.innerHTML = html;
        updateQuestionActions();
    };

    const renderThankYou = () => {
        content.innerHTML = `<p>${escapeHtml(t("DigitalPresence.Feedback.Widget.ThankYou", "Thank you for your feedback."))}</p>`;
        actions.hidden = false;
        actions.replaceChildren(
            createButton(t("DigitalPresence.Feedback.Widget.Done", "Done"), () => close(), "primary")
        );
    };

    toggle.addEventListener("click", () => {
        const isOpen = root.classList.contains("is-open");
        if (isOpen) {
            close();
            return;
        }

        open();
        renderCurrentQuestion();
    });

    closeButton.addEventListener("click", () => close());

    content.addEventListener("change", () => {
        const question = questions[currentIndex];
        if (!question) {
            return;
        }

        answers.set(question.id, collectAnswer(question));
        updateQuestionActions();
    });

    /** Keep the widget completely hidden until we know there is something to ask. */
    const hideWidget = () => {
        root.hidden = true;
    };
    const showWidget = () => {
        root.hidden = false;
    };

    root.hidden = true;

    const language = document.documentElement.lang;
    const response = await fetch(`/api/feedback/questions?language=${encodeURIComponent(language)}`);
    if (!response.ok) {
        hideWidget();
        return;
    }

    questions = (await response.json()) as Question[];

    if (questions.length === 0 || findVisibleIndex(0, 1) === -1) {
        hideWidget();
        return;
    }

    showWidget();
};

document.addEventListener("DOMContentLoaded", () => {
    void initializeFeedbackWidget();
});
